/**
 * /romaneios/novo e /romaneios/:id — Editor do Romaneio (Terceiros).
 *
 * Cliente/pedido/ambientes vem da planilha legada (useDados); os itens de
 * cada ambiente sao digitados aqui. Persiste no backend (/api/romaneios) e
 * gera o documento imprimivel (PDF + imprimir) so com a folha.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiCheck, FiDownload, FiEye, FiPlus, FiPrinter, FiX,
} from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useDados } from "../../context/DadosContext";
import { useToast } from "../../context/ToastContext";
import {
  atualizarRomaneio, criarRomaneio, emitirRomaneio, obterRomaneio,
} from "../../services/romaneios";
import { PageContainer, Section, SectionTitle } from "../../styles/shared";
import DocumentoRomaneio from "./DocumentoRomaneio";
import { baixarPdf, imprimirAgora } from "./impressao";
import { agruparPedidos, pedidoPorNumero } from "./romaneioPlanilha";
import {
  AddItemBtn, AmbienteBody, AmbienteCard, AmbienteHead, AmbienteNome, Card,
  CheckMark, ClienteHint, CountChip, DelBtn, DelSpacer, DocWrap, Dropdown,
  DropdownItem, ErrorMsg, Field, GhostBtn, HeadHint, InlineError, Input,
  ItemNum, ItemRow, Label, Notice, TextArea, Toolbar,
} from "./styles";

function montarAmbientes(planNomes, savedAmbientes, nextId) {
  const savedByNome = new Map((savedAmbientes || []).map((a) => [a.nome, a]));
  const nomes = [...(planNomes || [])];
  (savedAmbientes || []).forEach((a) => {
    if (!nomes.includes(a.nome)) nomes.push(a.nome);
  });
  return nomes.map((nome) => {
    const saved = savedByNome.get(nome);
    const included = !!saved;
    let rows = [];
    if (included) {
      const itens = saved.itens || [];
      rows = itens.length
        ? itens.map((it) => ({ id: nextId(), value: it.descricao }))
        : [{ id: nextId(), value: "" }];
    }
    return { nome, included, rows };
  });
}

export default function RomaneioEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  // Quem só tem romaneios:view consulta e imprime; salvar, mexer nos itens e
  // marcar como emitido são de romaneios:edit (mesmo gate do backend).
  const podeEditar = hasPermission("romaneios:edit");
  const { dados } = useDados();
  const toast = useToast();

  const uidRef = useRef(1);
  const nextId = useCallback(() => {
    uidRef.current += 1;
    return uidRef.current;
  }, []);

  const [romaneio, setRomaneio] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  const [pedidoSel, setPedidoSel] = useState("");
  const [cliente, setCliente] = useState("");
  const [marcenaria, setMarcenaria] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [ambientes, setAmbientes] = useState([]);

  const [pedidoQuery, setPedidoQuery] = useState("");
  const [showPedidoDD, setShowPedidoDD] = useState(false);
  const [errors, setErrors] = useState({});
  const [docOpen, setDocOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const blurTimer = useRef(null);

  // ---- carregar romaneio existente ----
  useEffect(() => {
    if (!id) return undefined;
    let cancel = false;
    obterRomaneio(request, id)
      .then((r) => {
        if (cancel) return;
        setRomaneio(r);
        setPedidoSel(r.num_pedido || "");
        setCliente(r.cliente_nome || "");
        setPedidoQuery(r.num_pedido || "");
        setMarcenaria(r.marcenaria || "");
        setObservacoes(r.observacoes || "");
      })
      .catch((e) => {
        if (!cancel) setLoadErr(e?.message || "Erro ao carregar romaneio.");
      });
    return () => {
      cancel = true;
    };
  }, [id, request]);

  // ---- montar ambientes (edit) a partir da planilha + snapshot salvo ----
  useEffect(() => {
    if (!id || !romaneio || touched) return;
    const plan = pedidoPorNumero(dados, romaneio.num_pedido)?.ambientes || [];
    setAmbientes(montarAmbientes(plan, romaneio.ambientes, nextId));
  }, [id, romaneio, dados, touched, nextId]);

  const pedidos = useMemo(() => agruparPedidos(dados), [dados]);
  const pedidoOpts = useMemo(() => {
    const q = pedidoQuery.trim().toLowerCase();
    return pedidos
      .filter((p) => !q || p.pedido.toLowerCase().includes(q) || p.cliente.toLowerCase().includes(q))
      .slice(0, 40);
  }, [pedidos, pedidoQuery]);

  const selectPedido = (p) => {
    setTouched(true);
    setPedidoSel(p.pedido);
    setCliente(p.cliente);
    setPedidoQuery(p.pedido);
    setShowPedidoDD(false);
    setAmbientes(montarAmbientes(p.ambientes, [], nextId));
    setErrors((e) => {
      const c = { ...e };
      delete c.pedido;
      return c;
    });
  };

  const toggleAmb = (idx) => {
    setTouched(true);
    setAmbientes((prev) =>
      prev.map((a, i) => {
        if (i !== idx) return a;
        const included = !a.included;
        const rows = included && a.rows.length === 0
          ? [{ id: nextId(), value: "" }]
          : a.rows;
        return { ...a, included, rows };
      }),
    );
    setErrors((e) => {
      const c = { ...e };
      delete c[`amb_${idx}`];
      delete c.ambientes;
      return c;
    });
  };

  const addItem = (idx) => {
    setTouched(true);
    setAmbientes((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, rows: [...a.rows, { id: nextId(), value: "" }] } : a)),
    );
  };

  const delItem = (idx, rowId) => {
    setTouched(true);
    setAmbientes((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, rows: a.rows.filter((r) => r.id !== rowId) } : a)),
    );
  };

  const setItem = (idx, rowId, value) => {
    setTouched(true);
    setAmbientes((prev) =>
      prev.map((a, i) =>
        i === idx ? { ...a, rows: a.rows.map((r) => (r.id === rowId ? { ...r, value } : r)) } : a,
      ),
    );
    setErrors((e) => {
      const c = { ...e };
      delete c[`amb_${idx}`];
      return c;
    });
  };

  function validar() {
    const e = {};
    if (!pedidoSel) e.pedido = "Selecione um pedido.";
    if (!marcenaria.trim()) e.marcenaria = "Informe a marcenaria terceira.";
    const incl = ambientes.filter((a) => a.included);
    if (pedidoSel && incl.length === 0) e.ambientes = "Selecione ao menos um ambiente.";
    ambientes.forEach((a, idx) => {
      if (a.included && !(a.rows.length && a.rows[0].value.trim())) {
        e[`amb_${idx}`] = "Preencha ao menos o primeiro item deste ambiente.";
      }
    });
    return e;
  }

  function payload() {
    return {
      num_pedido: pedidoSel,
      cliente_nome: cliente,
      marcenaria: marcenaria.trim(),
      observacoes: observacoes.trim(),
      ambientes: ambientes
        .filter((a) => a.included)
        .map((a) => ({
          nome: a.nome,
          itens: a.rows
            .map((r) => r.value.trim())
            .filter(Boolean)
            .map((d) => ({ descricao: d })),
        })),
    };
  }

  const salvar = async () => {
    const e = validar();
    if (Object.keys(e).length) {
      setErrors(e);
      throw new Error("Verifique os campos destacados.");
    }
    setErrors({});
    if (id) await atualizarRomaneio(request, id, payload());
    else await criarRomaneio(request, payload());
    toast.success("Romaneio salvo.");
    navigate("/romaneios");
  };

  const abrirDocumento = () => {
    if (!pedidoSel) {
      toast.error("Selecione um pedido para visualizar o documento.");
      return;
    }
    setDocOpen(true);
  };

  const marcarEmitido = async () => {
    if (podeEditar && id && romaneio && romaneio.situacao !== "EMITIDO") {
      try {
        const upd = await emitirRomaneio(request, id);
        setRomaneio(upd);
      } catch {
        /* silencioso — emitir eh best-effort */
      }
    }
  };

  const handleImprimir = () => {
    imprimirAgora();
    marcarEmitido();
  };

  const handlePdf = async () => {
    await baixarPdf(`Romaneio_${pedidoSel || "novo"}.pdf`);
    await marcarEmitido();
  };

  const docData = {
    numero: romaneio?.numero || "(novo)",
    cliente_nome: cliente,
    num_pedido: pedidoSel,
    marcenaria: marcenaria.trim(),
    emissao: new Date().toLocaleDateString("pt-BR"),
    observacoes: observacoes.trim(),
    ambientes: ambientes
      .filter((a) => a.included)
      .map((a) => ({ nome: a.nome, itens: a.rows.map((r) => r.value.trim()).filter(Boolean) }))
      .filter((a) => a.itens.length),
  };

  const editando = !!id;
  const semPlanilha = pedidos.length === 0;

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate("/romaneios")}
        title={editando ? (romaneio?.numero || "Romaneio") : "Novo romaneio"}
        subtitle="Carregamento em marcenaria terceira"
      />

      {loadErr && <ErrorMsg>{loadErr}</ErrorMsg>}

      <Toolbar>
        <GhostBtn type="button" onClick={abrirDocumento}>
          <FiEye size={14} /> Visualizar documento
        </GhostBtn>
        {podeEditar && (
          <AsyncButton variant="primary" onClick={salvar} silentError successMessage="">
            Salvar romaneio
          </AsyncButton>
        )}
      </Toolbar>

      {/* dados do romaneio */}
      <Card>
        <Field>
          <Label>Pedido</Label>
          {semPlanilha && !pedidoSel ? (
            <Notice>
              Nenhum pedido carregado da planilha. Abra a tela <strong>Entregas</strong> uma vez
              para carregar a planilha gerencial e volte aqui.
            </Notice>
          ) : (
            <>
              <Input
                value={pedidoQuery}
                placeholder="Buscar por nº do pedido ou cliente…"
                onChange={(e) => {
                  setPedidoQuery(e.target.value);
                  setShowPedidoDD(true);
                }}
                onFocus={() => setShowPedidoDD(true)}
                onBlur={() => {
                  clearTimeout(blurTimer.current);
                  blurTimer.current = setTimeout(() => setShowPedidoDD(false), 160);
                }}
              />
              {showPedidoDD && pedidoOpts.length > 0 && (
                <Dropdown>
                  {pedidoOpts.map((p) => (
                    <DropdownItem
                      key={p.pedido}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectPedido(p)}
                    >
                      Pedido {p.pedido}
                      <small>{p.cliente || "sem cliente"} · {p.ambientes.length} ambiente(s)</small>
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}
            </>
          )}
          {errors.pedido && <InlineError>{errors.pedido}</InlineError>}
          {pedidoSel && (
            <ClienteHint>
              Cliente: <span>{cliente || "—"}</span>
            </ClienteHint>
          )}
        </Field>

        <Field>
          <Label>Marcenaria terceira</Label>
          <Input
            value={marcenaria}
            placeholder="Nome da marcenaria que produziu os móveis"
            onChange={(e) => {
              setMarcenaria(e.target.value);
              setErrors((er) => {
                const c = { ...er };
                delete c.marcenaria;
                return c;
              });
            }}
          />
          {errors.marcenaria && <InlineError>{errors.marcenaria}</InlineError>}
        </Field>

        <Field>
          <Label>Observações</Label>
          <TextArea
            value={observacoes}
            placeholder="Observações gerais (opcional)"
            onChange={(e) => setObservacoes(e.target.value)}
          />
        </Field>
      </Card>

      {/* ambientes + itens */}
      <Section>
        <SectionTitle>Ambientes e itens</SectionTitle>
        {!pedidoSel ? (
          <Notice>Escolha um pedido acima para listar os ambientes.</Notice>
        ) : (
          <>
            {errors.ambientes && <InlineError>{errors.ambientes}</InlineError>}
            {ambientes.length === 0 ? (
              <Notice>Este pedido não tem ambientes na planilha.</Notice>
            ) : (
              ambientes.map((amb, idx) => (
                <AmbienteCard key={`${amb.nome}-${idx}`} $error={!!errors[`amb_${idx}`]}>
                  <AmbienteHead onClick={() => toggleAmb(idx)}>
                    <CheckMark $on={amb.included}>
                      {amb.included && <FiCheck size={13} />}
                    </CheckMark>
                    <AmbienteNome>{amb.nome}</AmbienteNome>
                    {amb.included && <CountChip>{amb.rows.length} item(ns)</CountChip>}
                    <HeadHint>{amb.included ? "incluído" : "incluir"}</HeadHint>
                  </AmbienteHead>

                  {amb.included && (
                    <AmbienteBody>
                      {amb.rows.map((row, i) => (
                        <ItemRow key={row.id}>
                          <ItemNum>{String(i + 1).padStart(2, "0")}</ItemNum>
                          <Input
                            value={row.value}
                            placeholder="Nome do móvel / item"
                            onChange={(e) => setItem(idx, row.id, e.target.value)}
                          />
                          {podeEditar && i > 0 ? (
                            <DelBtn
                              type="button"
                              title="Excluir item"
                              onClick={() => delItem(idx, row.id)}
                            >
                              <FiX size={14} />
                            </DelBtn>
                          ) : (
                            <DelSpacer />
                          )}
                        </ItemRow>
                      ))}
                      {errors[`amb_${idx}`] && (
                        <InlineError $indent>{errors[`amb_${idx}`]}</InlineError>
                      )}
                      {podeEditar && (
                        <AddItemBtn type="button" onClick={() => addItem(idx)}>
                          <FiPlus size={13} /> Adicionar item
                        </AddItemBtn>
                      )}
                    </AmbienteBody>
                  )}
                </AmbienteCard>
              ))
            )}
          </>
        )}
      </Section>

      {/* documento */}
      {docOpen && (
        <SlideOverPanel
          open
          onClose={() => setDocOpen(false)}
          title="Documento do romaneio"
          subtitle={docData.numero}
          width="min(96vw, 900px)"
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <GhostBtn type="button" onClick={handleImprimir}>
                <FiPrinter size={14} /> Imprimir
              </GhostBtn>
              <AsyncButton
                variant="primary"
                onClick={handlePdf}
                successMessage="PDF gerado."
                errorMessage="Falha ao gerar o PDF."
              >
                <FiDownload size={14} style={{ marginRight: 6 }} /> Baixar PDF
              </AsyncButton>
            </div>
          }
        >
          <DocWrap>
            <DocumentoRomaneio doc={docData} />
          </DocWrap>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
